import React from 'react';
import { 
  FileText, 
  Target, 
  TrendingUp, 
  Clock, 
  CheckCircle,
  Calendar,
  ExternalLink
} from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { useTenant } from '../contexts/TenantContext';

export interface Plan {
  id: string;
  name: string;
  currency: 'CLP' | 'USD' | 'EUR';
  value: number;
  description?: string;
  features?: string[];
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface Company {
  id: string;
  name: string;
  rut: string;
  email: string;
  phone: string;
  address: string;
  legal_representative?: string;
  legal_representative_rut?: string;
  technical_contact?: string;
  technical_email?: string;
  contact_phone?: string;
  plan_id?: string;
  created_at: string;
  updated_at?: string;
}

export interface Organization {
  id: string;
  name: string;
  organization_type_id: string;
  region?: string; // Mantener para compatibilidad
  comuna?: string; // Nuevo campo
  rut?: string; // Nuevo campo opcional
  contact?: string;
  email?: string;
  company_id: string;
  created_at: string;
  updated_at?: string;
}

export interface OrganizationType {
  id: string;
  name: string;
  description: string;
  created_at: string;
}

export interface TenderType {
  id: string;
  name: string;
  description?: string;
  category: 'goods' | 'services' | 'works' | 'consultancy';
  company_id: string;
  created_at: string;
  updated_at?: string;
}

export interface TenderStatus {
  id: string;
  name: string;
  description?: string;
  color: string;
  company_id?: string;
  created_at: string;
  updated_at?: string;
}

export interface ApplicationStatus {
  id: string;
  name: string;
  description?: string;
  color: string;
  order_index: number;
  company_id?: string;
  created_at: string;
  updated_at?: string;
}

// Interface simplificada para items de licitación
export interface TenderItem {
  id: string;
  CodigoProducto?: string;
  NombreProducto: string;
  UnidadMedida?: string;
  Cantidad: number;
  RutProveedor?: string;
  NombreProveedor?: string;
  MontoUnitario?: number;
}

export interface Tender {
  id: string;
  tender_code: string;
  title: string;
  description: string;
  organization_id: string;
  type_id: string;
  status_id: string;
  application_status_id?: string;
  amount: number;
  currency: 'CLP' | 'USD' | 'EUR';
  contract_months?: number;
  publish_date: string;
  closing_date: string;
  award_date?: string;
  requirements?: string;
  has_technical_visit: boolean;
  technical_visit_date?: string;
  has_gso: boolean;
  gso_amount?: number;
  gso_delivery_date?: string;
  has_gfc: boolean;
  gfc_amount?: number;
  gfc_delivery_date?: string;
  documents?: string[];
  fecha_preguntas?: string;
  // Campos para items simplificados
  items_count?: number; // Cantidad total de items solicitados
  items?: TenderItem[]; // Lista de items simplificados
  // Nuevo campo flujo
  flujo?: 'Disponible' | 'En Revision' | 'Aceptada' | 'Descartada' | 'Adjudicada';
  company_id: string;
  created_at: string;
  updated_at?: string;
}

export interface TenderDocument {
  id: string;
  tender_id: string;
  title: string;
  file_name: string;
  file_path: string;
  file_size?: number;
  mime_type?: string;
  uploaded_by?: string;
  company_id: string;
  created_at: string;
  updated_at?: string;
}

export interface Application {
  id: string;
  tender_id: string;
  status_id: string;
  submission_date: string;
  amount: number;
  currency: 'CLP' | 'USD' | 'EUR';
  proposal: string;
  documents: string[];
  documents_metadata?: ApplicationDocument[];
  cost_items?: Array<{
    id: string;
    product_id: string;
    quantity: number;
    months: number;
  }>;
  fecha_preguntas?: string;
  notes: string;
  company_id: string;
  created_at: string;
  updated_at?: string;
}

export interface ApplicationDocument {
  id: string;
  title: string;
  type: 'administrativos' | 'tecnicos' | 'financieros' | 'otros';
  file_name: string;
  file_size?: number;
  required: boolean;
  signed?: boolean;
  uploaded_at?: string;
}

export interface Product {
  id: string;
  code: string;
  description: string;
          currency: 'CLP' | 'USD' | 'CLF';
  unit_value: number;
  company_id: string;
  created_at: string;
  updated_at?: string;
  createdAt: string; // Alias for created_at for compatibility
}

export interface CurrencyRate {
  id: string;
          currency: 'USD' | 'EUR' | 'CLF';
  rate: number;
  date: string;
  company_id: string;
  created_at: string;
  updated_at?: string;
}

export interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface User {
  id: string;
  email: string;
  app_metadata: {
    provider?: string;
    [key: string]: any;
  };
  user_metadata: {
    name?: string;
    [key: string]: any;
  };
  aud: string;
  created_at: string;
}

export interface Employee {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'user';
  is_active: boolean;
  company_id: string;
  created_by?: string;
  created_by_name?: string;
  company_name?: string;
  created_at: string;
  updated_at?: string;
}

export interface CreateEmployeeData {
  name: string;
  email: string;
  password: string;
  role: 'admin' | 'user';
}

export interface ChangePasswordData {
  old_password: string;
  new_password: string;
}

// Nuevas interfaces para Mi Cuenta
export interface BusinessLine {
  id: string;
  name: string;
  keywords: string[];
  regions: string[];
  sectors: string[];
  organizations: string[]; // IDs de organizaciones de interés
  tender_types: ('licitaciones' | 'compra_agil' | 'trato_directo')[];
  is_active: boolean;
  company_id: string;
  created_at: string;
  updated_at?: string;
}

export interface AccountSettings {
  id: string;
  company_id: string;
  business_lines: BusinessLine[];
  notification_preferences: {
    email_alerts: boolean;
    sms_alerts: boolean;
    push_notifications: boolean;
  };
  search_preferences: {
    auto_search_enabled: boolean;
    search_frequency: 'daily' | 'weekly' | 'monthly';
    min_amount: number;
    max_amount: number;
  };
  created_at: string;
  updated_at?: string;
}

const Dashboard: React.FC = () => {
  const { 
    tenders, 
    applications, 
    organizations, 
    applicationStatuses 
  } = useData();
  const { currentCompany } = useTenant();

  // Filtrar por la compañía actual
  const companyTenders = tenders.filter(tender => tender.company_id === currentCompany?.id);
  const companyApplications = applications.filter(app => app.company_id === currentCompany?.id);

  // Licitaciones en revisión
  const tendersInReview = companyTenders.filter(tender => tender.flujo === 'En Revision');

  // Postulaciones en preparación (aplicaciones con status "En Preparación")
  const preparationStatus = applicationStatuses.find(status => status.name === 'En Preparacion');
  const applicationsInPreparation = companyApplications.filter(app => app.status_id === preparationStatus?.id);

  // Postulaciones en preparación que vencen en los próximos 7 días
  const urgentApplicationsInPreparation = applicationsInPreparation.filter(app => {
    const tender = companyTenders.find(t => t.id === app.tender_id);
    if (!tender?.closing_date) return false;
    
    const closingDate = new Date(tender.closing_date);
    const now = new Date();
    const diffDays = Math.ceil((closingDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return diffDays <= 7 && diffDays > 0;
  });

  const stats = [
    {
      title: 'Licitaciones Nuevas',
      value: companyTenders.filter(tender => tender.flujo === 'Disponible' || tender.flujo === 'En Revision').length.toLocaleString(),
      icon: FileText,
      color: 'bg-blue-500',
      textColor: 'text-blue-600'
    },
    {
      title: 'Total Oportunidades',
      value: companyApplications.length.toLocaleString(),
      icon: Target,
      color: 'bg-green-500',
      textColor: 'text-green-600'
    },
    {
      title: 'Oportunidades en Revisión',
      value: applicationsInPreparation.length.toLocaleString(),
      icon: Clock,
      color: 'bg-purple-500',
      textColor: 'text-purple-600'
    },
    {
      title: 'Tasa de Éxito',
      value: (() => {
        const adjudicatedApplications = companyApplications.filter(app => 
          applicationStatuses.find(status => status.id === app.status_id)?.name === 'Adjudicada'
        );
        const totalAmount = adjudicatedApplications.reduce((sum, app) => sum + app.amount, 0);
        const percentage = companyApplications.length > 0 
          ? (adjudicatedApplications.length / companyApplications.length * 100).toLocaleString(undefined, {maximumFractionDigits: 1})
          : 0;
        
        return {
          main: `${totalAmount.toLocaleString('es-CL')} CLP`,
          subtitle: `${adjudicatedApplications.length} licitaciones • ${percentage}%`
        };
      })(),
      icon: TrendingUp,
      color: 'bg-orange-500',
      textColor: 'text-orange-600'
    }
  ];

  const recentApplications = applicationsInPreparation
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  const orderedRecentApplications = [...recentApplications].sort((a, b) => {
    const tenderA = companyTenders.find(t => t.id === a.tender_id);
    const tenderB = companyTenders.find(t => t.id === b.tender_id);
    if (!tenderA || !tenderB) return 0;
    return new Date(tenderA.closing_date).getTime() - new Date(tenderB.closing_date).getTime();
  });

  // Datos para el gráfico de estados del Kanban
  const kanbanData = [
    {
      name: 'En Preparación',
      count: companyApplications.filter(app => 
        applicationStatuses.find(status => status.id === app.status_id)?.name === 'En Preparacion'
      ).length,
      color: 'bg-blue-500'
    },
    {
      name: 'Ofertada',
      count: companyApplications.filter(app => 
        applicationStatuses.find(status => status.id === app.status_id)?.name === 'Ofertada'
      ).length,
      color: 'bg-green-500'
    },
    {
      name: 'Descartada',
      count: companyApplications.filter(app => 
        applicationStatuses.find(status => status.id === app.status_id)?.name === 'Descartada'
      ).length,
      color: 'bg-gray-500'
    },
    {
      name: 'Adjudicada',
      count: companyApplications.filter(app => 
        applicationStatuses.find(status => status.id === app.status_id)?.name === 'Adjudicada'
      ).length,
      color: 'bg-orange-500'
    },
    {
      name: 'No Adjudicada',
      count: companyApplications.filter(app => 
        applicationStatuses.find(status => status.id === app.status_id)?.name === 'No Adjudicada'
      ).length,
      color: 'bg-red-500'
    }
  ];

  const maxCount = Math.max(...kanbanData.map(item => item.count));


  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Panel de Oportunidades</h1>
        <p className="text-gray-600">Vista general de licitaciones y postulaciones de {currentCompany?.name}</p>
      </div>

      {/* Estadísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {stats.map((stat, index) => {
          const Icon = stat.icon;
          return (
            <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  {typeof stat.value === 'object' ? (
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{stat.value.main}</p>
                      <p className="text-sm text-gray-500 mt-1">{stat.value.subtitle}</p>
                    </div>
                  ) : (
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  )}
                </div>
                <div className={`p-3 rounded-full ${stat.color.replace('500', '100')}`}>
                  <Icon className={`h-6 w-6 ${stat.textColor}`} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Gráfico de Estados del Kanban */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Distribución por Estados</h2>
          <p className="text-sm text-gray-600">Total de postulaciones por estado del Kanban</p>
        </div>
        
        <div className="space-y-4">
          {kanbanData.map((item, index) => {
            const percentage = maxCount > 0 ? (item.count / maxCount) * 100 : 0;
            return (
              <div key={index} className="flex items-center">
                <div className="w-24 text-sm font-medium text-gray-700">
                  {item.name}
                </div>
                <div className="flex-1 mx-4">
                  <div className="w-full bg-gray-200 rounded-full h-6">
                    <div 
                      className={`h-6 rounded-full ${item.color} transition-all duration-300 ease-in-out`}
                      style={{ width: `${percentage}%` }}
                    />
                  </div>
                </div>
                <div className="w-16 text-sm font-semibold text-gray-900 text-right">
                  {item.count}
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="mt-6 pt-4 border-t border-gray-200">
          <div className="flex justify-between text-sm text-gray-600">
            <span>Total: {companyApplications.length} postulaciones</span>
            <span>Máximo: {maxCount}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Postulaciones Recientes */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Postulaciones Recientes</h2>
          </div>
          <div className="divide-y divide-gray-200">
            {orderedRecentApplications.length > 0 ? (
              orderedRecentApplications.map((application) => {
                const tender = companyTenders.find(t => t.id === application.tender_id);
                const organization = organizations.find(org => org.id === tender?.organization_id);
                const status = applicationStatuses.find(s => s.id === application.status_id);
                return (
                  <div key={application.id} className="p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {tender?.title || 'Licitación no encontrada'}
                        </p>
                        <p className="text-sm text-gray-500">
                          {organization?.name || 'Organización no encontrada'}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex flex-col space-y-1">
                            <p className="text-xs text-blue-600">
                              <span className="font-bold">Presupuesto:</span> {tender?.currency} {tender?.amount ? Math.round(tender.amount).toLocaleString('es-CL') : '0'}
                            </p>
                            <p className="text-xs text-green-600">
                              <span className="font-medium">Ofertado:</span> {application.currency} {application.amount ? Math.round(application.amount).toLocaleString('es-CL') : '0'}
                            </p>
                          </div>
                          <div className="flex flex-col items-end space-y-1">
                            <p className="text-xs text-gray-600">
                              <span className="font-medium">Cierre:</span> {tender?.closing_date ? new Date(tender.closing_date).toLocaleDateString('es-ES') : 'N/A'}
                            </p>
                            {tender?.closing_date && (
                              <p className="text-xs font-medium text-red-600">
                                {(() => {
                                  const closingDate = new Date(tender.closing_date);
                                  const now = new Date();
                                  const diffDays = Math.ceil((closingDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
                                  if (diffDays > 0) {
                                    return `${diffDays} días restantes`;
                                  } else if (diffDays === 0) {
                                    return 'Cierra hoy';
                                  } else {
                                    return 'Cerrada';
                                  }
                                })()}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="ml-4 flex-shrink-0 flex items-center space-x-2">
                        {tender?.tender_code && (
                          <button
                            onClick={() => window.open(`https://www.mercadopublico.cl/fichaLicitacion.html?idLicitacion=${tender.tender_code}`, '_blank')}
                            className="p-1 rounded transition-colors text-gray-400 hover:text-blue-600 hover:bg-blue-50"
                            title="Ver en Mercado Público"
                          >
                            <ExternalLink className="h-3 w-3" />
                          </button>
                        )}
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                          {status?.name || 'En Revision'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center text-gray-500">
                <Target className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                <p>No hay postulaciones en preparación</p>
              </div>
            )}
          </div>
        </div>

        {/* Licitaciones en Revisión */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center">
              <Clock className="h-5 w-5 mr-2 text-purple-500" />
              Licitaciones Disponibles y en Revisión
            </h2>
            <p className="text-sm text-gray-600">Requieren análisis y decisión</p>
          </div>
          <div className="divide-y divide-gray-200">
            {(() => {
              const disponibles = companyTenders.filter(t => t.flujo === 'Disponible');
              const enRevision = companyTenders.filter(t => t.flujo === 'En Revision');
              const ordered = [...disponibles, ...enRevision];
              return ordered.length > 0 ? (
                ordered.map((tender) => {
                  const organization = organizations.find(org => org.id === tender.organization_id);
                  const daysLeft = Math.ceil((new Date(tender.closing_date).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                  return (
                    <div key={tender.id} className="p-4 hover:bg-gray-50">
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {tender.title}
                          </p>
                          <p className="text-sm text-gray-500">
                            {organization?.name || 'Organización no encontrada'}
                          </p>
                          <div className="flex items-center mt-1">
                            <Clock className={`h-3 w-3 mr-1 ${tender.flujo === 'Disponible' ? 'text-blue-500' : 'text-purple-500'}`} />
                            <span className={`text-xs font-medium ${tender.flujo === 'Disponible' ? 'text-blue-600' : 'text-purple-600'}`}> 
                              {daysLeft > 0 ? `${daysLeft} días restantes` : 'Cerrada'}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4 flex-shrink-0">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${tender.flujo === 'Disponible' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}> 
                            {tender.flujo}
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="p-8 text-center text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                  <p>No hay licitaciones disponibles ni en revisión</p>
                </div>
              );
            })()}
          </div>
        </div>
      </div>

      {/* Resumen de Actividad */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Resumen de Actividad</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-blue-50 rounded-lg p-4 border border-blue-100">
            <div className="flex items-center mb-2">
              <Calendar className="h-5 w-5 text-blue-600 mr-2" />
              <h4 className="font-medium text-blue-900">Próximos Vencimientos</h4>
            </div>
            <p className="text-sm text-blue-800">
              {urgentApplicationsInPreparation.length > 0 
                ? `Tienes ${urgentApplicationsInPreparation.length} postulaciones en preparación que vencen los próximos 7 días que requieren análisis.` 
                : 'No tienes postulaciones en preparación que venzan pronto.'}
            </p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-4 border border-green-100">
            <div className="flex items-center mb-2">
              <Target className="h-5 w-5 text-green-600 mr-2" />
              <h4 className="font-medium text-green-900">Postulaciones Activas</h4>
            </div>
            <p className="text-sm text-green-800">
              {companyApplications.filter(app => {
                const status = applicationStatuses.find(s => s.id === app.status_id);
                return status?.name === 'Ofertada';
              }).length} postulaciones ofertadas actualmente.
            </p>
          </div>
          
          <div className="bg-purple-50 rounded-lg p-4 border border-purple-100">
            <div className="flex items-center mb-2">
              <Clock className="h-5 w-5 text-purple-600 mr-2" />
              <h4 className="font-medium text-purple-900">En Revision</h4>
            </div>
            <p className="text-sm text-purple-800">
              {tendersInReview.length} licitaciones requieren análisis y decisión.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;